import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import CurrencyInputField from 'react-currency-input-field';
import { ParagraphNormal } from './Paragraph';
import {
  MINUS_SYMBOL,
  PLUS_SYMBOL,
  NUMPAD_MINUS_SYMBOL,
  NUMPAD_PLUS_SYMBOL,
  K_KEYCODE,
} from '../constants/keyCodeConstants';
import { formatMoney } from '../util/helpers/numeralHelpers';
import themeColors from '../assets/styles/themeColors';
import { variables } from '../assets/styles/variables';

const Label = styled(ParagraphNormal)`
  line-height: 2.1;
  font-weight: 600;
  height: 29px;
`;
const TextInputField = styled(CurrencyInputField)`
  padding: ${({ hasPrefix }) => (hasPrefix ? '12px 24px' : '12px')};
  border: 1px solid
    ${({ hasError }) =>
      hasError
        ? themeColors.colorErrorMessage
        : themeColors.colorBorderPrimary};
  border-width: 1px;
  border-style: solid;
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorTextSecondary};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: ${({ hasSuggestion }) =>
    hasSuggestion ? 'capitalize' : 'none'};
  outline: none;
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  background: transparent;
  z-index: ${({ hasSuggestion }) => (hasSuggestion ? 1 : 'initial')};

  &:focus {
    border-color: ${themeColors.colorSvgSemidark};
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondaryLight};
  }
`;
const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: ${({ width }) => width ?? '100%'};
  position: relative;
`;
const ErrorMessage = styled(ParagraphNormal)`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

const CurrencyInput = ({
  autoFocus,
  notCentered,
  notBold,
  label,
  onChange,
  value,
  error,
  ...props
}) => {
  const inputField = useRef(null);
  useEffect(() => {
    if (autoFocus) {
      inputField.current.focus();
    }
  }, [autoFocus, inputField]);

  const onKeydownHandler = (event) => {
    if (
      event.keyCode === MINUS_SYMBOL ||
      event.keyCode === PLUS_SYMBOL ||
      event.keyCode === NUMPAD_MINUS_SYMBOL ||
      event.keyCode === NUMPAD_PLUS_SYMBOL ||
      event.keyCode === K_KEYCODE
    ) {
      event.preventDefault();
    }
  };

  const prefix = formatMoney(0);
  const prefixSymbol = () => {
    if (prefix.includes('CAD')) {
      return 'CAD ';
    }

    return '$';
  };

  const onValueChange = (value) => {
    onChange({
      target: {
        value,
      },
    });
  };

  return (
    <InputContainer>
      {!!label && <Label>{label}</Label>}
      {value ? (
        <TextInputField
          {...props}
          prefix={prefixSymbol()}
          onValueChange={onValueChange}
          onKeyDown={onKeydownHandler}
          ref={inputField}
          defaultValue={0}
          value={value}
          decimalsLimit={2}
        />
      ) : (
        <TextInputField
          {...props}
          prefix={prefixSymbol()}
          onValueChange={onValueChange}
          onKeyDown={onKeydownHandler}
          ref={inputField}
          decimalsLimit={2}
        />
      )}
      {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
    </InputContainer>
  );
};

CurrencyInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
  notCentered: PropTypes.bool,
  notBold: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
};

export default CurrencyInput;
